import { useEffect, useState } from "react";
import {
  approveLinkOffer,
  createLinkOffer,
  createLocalDevice,
  decodeLinkOffer,
  encodeLinkOffer,
  revokeDevice,
  userFacingMessage,
  verifyLinkApproval,
} from "@veto/domain";
import { verifyMessagingSig } from "@veto/crypto-bridge";
import type { DeviceRecord } from "@veto/protocol";
import { DEVICE_FEATURE_STATUS } from "@veto/protocol";
import { getMeta, setMeta } from "@veto/storage";

const META_KEY = "devices.registry.v1";
const SELF_KEY = "devices.self.v1";

async function loadDevices(): Promise<DeviceRecord[]> {
  const raw = await getMeta(META_KEY);
  if (!raw) return [];
  try {
    return JSON.parse(raw) as DeviceRecord[];
  } catch {
    return [];
  }
}

async function saveDevices(devices: DeviceRecord[]): Promise<void> {
  await setMeta(META_KEY, JSON.stringify(devices));
}

export function LinkedDevicesPanel({
  accountFingerprint,
  assuranceMode,
  onToast,
  signWithIdentity,
}: {
  accountFingerprint: string;
  assuranceMode: string;
  onToast: (msg: string) => void;
  /** Ed25519 signer bound to the unlocked account identity (WASM). Null while locked. */
  signWithIdentity: ((message: Uint8Array) => Uint8Array) | null;
}) {
  const [devices, setDevices] = useState<DeviceRecord[]>([]);
  const [selfId, setSelfId] = useState<string | null>(null);
  const [offerPayload, setOfferPayload] = useState<string | null>(null);
  const [paste, setPaste] = useState("");
  const [busy, setBusy] = useState(false);
  // deviceId → approval states: true verified, false INVALID, null legacy/unknown.
  const [approvalState, setApprovalState] = useState<Record<string, boolean | null>>({});

  useEffect(() => {
    void (async () => {
      let list = await loadDevices();
      let self = await getMeta(SELF_KEY);
      if (!self || list.length === 0) {
        const local = createLocalDevice({
          name: "This device",
          platform: assuranceMode === "pwa" ? "web" : "android",
          fingerprintHex: accountFingerprint.slice(0, 64),
        });
        list = [local, ...list.filter((d) => d.fingerprint !== local.fingerprint)];
        await saveDevices(list);
        await setMeta(SELF_KEY, local.deviceId);
        self = local.deviceId;
      }
      setSelfId(self);
      setDevices(list);
      // FR-DEV-001: re-verify stored approval certificates on every load so
      // registry tampering surfaces instead of sitting silently trusted.
      const states: Record<string, boolean | null> = {};
      for (const d of list) {
        if (d.deviceId === self) continue;
        try {
          states[d.deviceId] = verifyLinkApproval(
            d,
            accountFingerprint,
            (pk: Uint8Array, message: Uint8Array, sig: Uint8Array) =>
              verifyMessagingSig(pk, message, sig),
          );
        } catch {
          states[d.deviceId] = false;
        }
      }
      setApprovalState(states);
    })();
  }, [accountFingerprint, assuranceMode]);

  async function startLink() {
    const offer = createLinkOffer({
      newDeviceFingerprint: randomFp(),
      platform: "web",
      name: "New Veto device",
    });
    const payload = encodeLinkOffer(offer);
    setOfferPayload(payload);
    onToast("Share this link code with your primary device to approve");
  }

  async function approvePasted() {
    // Read live textarea value so paste/autofill cannot leave a filled-but-disabled control.
    const live =
      paste.trim() ||
      (typeof document !== "undefined"
        ? (document.getElementById("approveOffer") as HTMLTextAreaElement | null)?.value?.trim() || ""
        : "");
    if (!selfId || !live) {
      onToast("Paste a link code first");
      return;
    }
    // FR-DEV-001: approvals are Ed25519-signed by the unlocked account identity.
    // Refuse unsigned approvals fail-closed instead of minting trust theater.
    if (!signWithIdentity) {
      onToast("Unlock the vault first — approvals need the account identity");
      return;
    }
    if (live !== paste) setPaste(live);
    setBusy(true);
    try {
      const offer = decodeLinkOffer(live);
      const { devices: next } = approveLinkOffer(devices, offer, selfId, Date.now(), signWithIdentity);
      await saveDevices(next);
      setDevices(next);
      const linked = next.find((d) => !devices.some((x) => x.deviceId === d.deviceId));
      if (linked) {
        try {
          setApprovalState((prev) => ({
            ...prev,
            [linked.deviceId]: verifyLinkApproval(
              linked,
              accountFingerprint,
              (pk: Uint8Array, message: Uint8Array, sig: Uint8Array) =>
              verifyMessagingSig(pk, message, sig),
            ),
          }));
        } catch {
          /* displayed as unknown; registry holds the signed record */
        }
      }
      setPaste("");
      onToast("Device linked — approval signed; sessions rotate on revoke");
    } catch (e) {
      onToast(userFacingMessage(e));
    } finally {
      setBusy(false);
    }
  }

  async function onRevoke(deviceId: string) {
    try {
      const { devices: next, rotateSessions } = revokeDevice(devices, deviceId);
      await saveDevices(next);
      setDevices(next);
      if (rotateSessions) {
        // FR-DEV-003: in-memory + legacy sessionStorage cache cleared here;
        // persisted ratchet states are wiped durably by useSessionRotateOnRevoke.
        const keys = Object.keys(sessionStorage).filter((k) => k.startsWith("veto.hs."));
        for (const k of keys) sessionStorage.removeItem(k);
        await setMeta("devices.rotateSessionsAt", String(Date.now()));
        onToast("Device revoked — local sessions cleared; re-open chats to renegotiate");
      } else {
        onToast("Revoked");
      }
    } catch (e) {
      onToast(userFacingMessage(e));
    }
  }

  return (
    <div className="stack">
      <h3>Linked devices</h3>
      <p className="muted">{DEVICE_FEATURE_STATUS.gateMessage}</p>
      {devices.map((d) => {
        const approval = d.deviceId === selfId ? null : (approvalState[d.deviceId] ?? null);
        return (
        <div key={d.deviceId} className="device-card member-card">
          <div>
            <strong>
              {d.name}
              {d.deviceId === selfId ? " (this device)" : ""}
            </strong>
            <div className="event-meta">
              {d.platform} · {d.trustState} · {d.fingerprint.slice(0, 16)}…
              {approval === true ? " · approval verified" : ""}
              {approval === false ? " · approval INVALID — revoke and re-link" : ""}
            </div>
          </div>
          {d.deviceId !== selfId && d.trustState !== "revoked" && (
            <button type="button" className="btn danger small" onClick={() => void onRevoke(d.deviceId)}>
              Revoke
            </button>
          )}
        </div>
        );
      })}
      <button className="btn" type="button" onClick={() => void startLink()}>
        Generate link code
      </button>
      {offerPayload && (
        <div className="field">
          <label htmlFor="linkOffer">Link payload (QR text)</label>
          <textarea id="linkOffer" readOnly rows={3} value={offerPayload} />
          <button
            type="button"
            className="btn secondary"
            onClick={() => void navigator.clipboard.writeText(offerPayload)}
          >
            Copy
          </button>
        </div>
      )}
      <div className="field">
        <label htmlFor="approveOffer">Approve another device’s code</label>
        <textarea
          id="approveOffer"
          rows={3}
          value={paste}
          onChange={(e) => setPaste(e.target.value)}
          onInput={(e) => setPaste((e.target as HTMLTextAreaElement).value)}
          onPaste={(e) => {
            const text = e.clipboardData?.getData("text") ?? "";
            if (text) {
              // Ensure React state catches paste in the same tick as the enable check.
              window.setTimeout(() => {
                const live =
                  (document.getElementById("approveOffer") as HTMLTextAreaElement | null)?.value ?? "";
                if (live !== paste) setPaste(live);
              }, 0);
            }
          }}
          placeholder="Paste veto-link:… payload"
          aria-describedby="approveOfferHint"
        />
        <ApproveLinkControls busy={busy} paste={paste} onApprove={() => void approvePasted()} />
      </div>
    </div>
  );
}

function ApproveLinkControls({
  busy,
  paste,
  onApprove,
}: {
  busy: boolean;
  paste: string;
  onApprove: () => void;
}) {
  const live =
    paste.trim() ||
    (typeof document !== "undefined"
      ? (document.getElementById("approveOffer") as HTMLTextAreaElement | null)?.value?.trim() || ""
      : "");
  const canApprove = !!live && !busy;
  return (
    <>
      <button
        className={`btn${canApprove ? " approve-ready" : ""}`}
        type="button"
        disabled={!canApprove}
        aria-disabled={!canApprove}
        title={canApprove ? "Approve pasted device link" : "Paste a veto-link offer first"}
        onClick={onApprove}
      >
        {busy ? "Approving…" : canApprove ? "Approve link" : "Paste code to approve"}
      </button>
      <p id="approveOfferHint" className="muted text-xs" style={{ margin: 0 }} role="status">
        {canApprove
          ? "Link code ready — approve to add this device to your registry."
          : "Approve enables after you paste a veto-link: offer."}
      </p>
    </>
  );
}

function randomFp(): string {
  const a = new Uint8Array(32);
  crypto.getRandomValues(a);
  return [...a].map((b) => b.toString(16).padStart(2, "0")).join("");
}
